import type { Clip } from '@/types'

/**
 * Everything copy/paste carries from a clip. Timeline position is stored relative to the
 * selection's first clip and lowest track so a paste can land anywhere.
 *
 * Timing fields travel together: `loop` decides whether `duration` is bound to the source window
 * (`trimIn`/`trimOut`/`speed`), so dropping it would paste a duration the store cannot explain.
 */
export interface TimelineClipboardItem {
  assetId: string
  trackOffset: number
  startOffset: number
  duration: number
  trimIn: number
  flipHorizontal?: boolean
  flipVertical?: boolean
  trimOut: number
  speed?: number
  preservePitch?: boolean
  loop?: boolean
  gainDb?: number
  fadeIn?: number
  fadeOut?: number
  volumePoints?: Clip['volumePoints']
  nestedSequenceId?: string
  linkGroupId?: string
}

/** The clip the store receives for one pasted item. */
export type PastedClipSpec = Omit<TimelineClipboardItem, 'trackOffset' | 'startOffset'> & {
  trackIndex: number
  startOnTimeline: number
}

export function clipboardItemFromClip(
  clip: Clip,
  origin: { startOnTimeline: number; trackIndex: number },
): TimelineClipboardItem {
  return {
    assetId: clip.assetId,
    trackOffset: clip.trackIndex - origin.trackIndex,
    startOffset: clip.startOnTimeline - origin.startOnTimeline,
    duration: clip.duration,
    trimIn: clip.trimIn,
    trimOut: clip.trimOut,
    flipHorizontal: clip.flipHorizontal,
    flipVertical: clip.flipVertical,
    speed: clip.speed,
    preservePitch: clip.preservePitch,
    loop: clip.loop,
    gainDb: clip.gainDb,
    fadeIn: clip.fadeIn,
    fadeOut: clip.fadeOut,
    volumePoints: clip.volumePoints,
    nestedSequenceId: clip.nestedSequenceId,
    linkGroupId: clip.linkGroupId,
  }
}

export function pastedClipFromItem(
  item: TimelineClipboardItem,
  target: { startOnTimeline: number; trackIndex: number; linkGroupId: string | undefined },
): PastedClipSpec {
  return {
    assetId: item.assetId,
    trackIndex: target.trackIndex,
    startOnTimeline: target.startOnTimeline,
    duration: item.duration,
    trimIn: item.trimIn,
    trimOut: item.trimOut,
    flipHorizontal: item.flipHorizontal,
    flipVertical: item.flipVertical,
    speed: item.speed,
    preservePitch: item.preservePitch,
    loop: item.loop,
    gainDb: item.gainDb,
    fadeIn: item.fadeIn,
    fadeOut: item.fadeOut,
    volumePoints: item.volumePoints,
    nestedSequenceId: item.nestedSequenceId,
    linkGroupId: target.linkGroupId,
  }
}
